import { v4 as uuid } from "uuid";
import { db } from "../db";
import updateImageDoc from "../../utils/_updateImageDoc";
import { getImageBinaryById, getImageBinaryOnline } from "./imageBinaryQueries";
import { FieldInstance, Image } from "db/definitions";

/**
 * Given an array of image document ids, deletes all of them from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids An array of ids
 */
export async function bulkDeleteImageDocs(ids: string[]): Promise<void> {
	await db.image.bulkDelete(ids);
}

/**
 * Given an array of ids, queries and retrieves the image documents from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids An array of ids to query
 * @returns All image documents from the array of ids
 */
export async function bulkGetImageDocs(ids: string[]): Promise<Image[]> {
	const imageDocs = await db.image.bulkGet(ids);
	const filtered = imageDocs.filter((img): img is Image => img !== undefined);
	return filtered;
}

/**
 * Given a formGroup id, queries all images in the indexedDb by it and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroupId The id of the formGroup that has the image
 * @returns All images within the formGroup
 */
export async function bulkRetrieveImagesByFormGroup(formGroupId: string): Promise<Image[]> {
	const images = await db.image.where("formGroup").equals(formGroupId).toArray();
	return images;
}

/**
 * Given a project id, queries all images in the project with that id and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param projectId The id of the project
 * @returns All images in the indexeddb with the given project id
 */
export async function bulkRetrieveImagesByProject(projectId: string): Promise<Image[]> {
	const images = await db.image.where("project").equals(projectId).toArray();
	return images;
}

/**
 * Given an array of image objects, saves them to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param images The image objects we would like to save
 */
export async function bulkSaveImageDocs(images: Image[]): Promise<void> {
	await db.image.bulkPut(images);
}

/**
 * Given an image document, parses it down to its base properties and return it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param image The image we want to parse
 * @returns The image document
 */
export function createImageForBatchResolver(image: Image): Image {
	const parsedImage: Image = {
		id: image.id,
		formGroup: image.formGroup,
		project: image.project,
		aspectX: image.aspectX || "",
		aspectY: image.aspectY || "",
		group: image.group,
		createdAt: image.createdAt || new Date().toISOString(),
		updatedAt: image.updatedAt || new Date().toISOString(),
		rotation: image.rotation || "",
		dpi: image.dpi || "",
		key: image.key || "",
	};
	return parsedImage;
}

/**
 * Given the fieldInstance, extracts the relevant info for the image document, gets the image binary, and saves both offline
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldInstance The fieldInstance with an imageDocument
 * @param direction The direction we need to save the image
 * @param online Whether or not the user is online
 * @param shouldSavePhotos
 */
export async function createImageAndBinaryDocs(
	fieldInstance: FieldInstance,
	direction: "UP" | "DOWN",
	online: boolean,
	shouldSavePhotos: boolean,
): Promise<void> {
	if (fieldInstance.image) {
		const image = createImageForBatchResolver(fieldInstance.image);
		if (shouldSavePhotos && typeof image.key === "string") {
			const imageBinary = await getImageBinaryOnline(image.key);
			if (imageBinary) {
				image.key = { files: [imageBinary] };
				const key = imageBinary.name.replace(/\.[^/.]+$/, "");
				await updateImageDoc({ key }, image, fieldInstance.group, direction, online, true);
			} else {
				// Uh oh? They want to save photos, but the photo doesn't exist, so we just save the doc itself?
				await saveImageDoc(image);
			}
		} else {
			await updateImageDoc({ key: uuid() }, image, fieldInstance.group, direction, online, true);
		}
	}
}

/**
 * Given 2 image documents, merges their properties
 * @author Eric Webb <ewebb@factorearth.com>
 * @param leftImage The "donor" image document
 * @param rightImage The "receiver" image document
 * @returns A parsed, merged version of the 2 documents
 */
export function createdMergedImageDocForResolver(leftImage: Image, rightImage: Image): { image: Image; keyToDelete: string; keyToSave: string } {
	// using EPOCH so that if one has a correct updatedAt and the other is missing (though it shouldnt be) the correct image isnt being deleted
	const timeStamp = new Date(0);
	const leftTimeStamp = leftImage.updatedAt ? new Date(leftImage.updatedAt) : timeStamp;
	const rightTimeStamp = rightImage.updatedAt ? new Date(rightImage.updatedAt) : timeStamp;
	const evaluation = leftTimeStamp > rightTimeStamp;
	let keyToDelete = "";
	let keyToSave = "";

	const rawWinnerKey = evaluation ? leftImage.key : rightImage.key;
	const rawLoserKey = evaluation ? rightImage.key : leftImage.key;
	const winnerKey = typeof rawWinnerKey === "string" ? rawWinnerKey : "";
	const loserKey = typeof rawLoserKey === "string" ? rawLoserKey : "";

	if (leftImage.key !== rightImage.key) {
		if (winnerKey) {
			keyToSave = winnerKey;
			keyToDelete = loserKey; // loserKey may be "" — that's fine
		} else if (loserKey) {
			keyToSave = loserKey; // winner empty, keep the only valid key
		}
	}

	const imageToReturn: Image = {
		id: evaluation ? leftImage.id : rightImage.id,
		formGroup: evaluation ? leftImage.formGroup : rightImage.formGroup,
		project: evaluation ? leftImage.project : rightImage.project,
		key: keyToSave || winnerKey || "", // ← uses best available key
		rotation: evaluation ? leftImage.rotation : rightImage.rotation,
		dpi: evaluation ? leftImage.dpi : rightImage.dpi,
		aspectX: evaluation ? leftImage.aspectX : rightImage.aspectX,
		aspectY: evaluation ? leftImage.aspectY : rightImage.aspectY,
		group: evaluation ? leftImage.group : rightImage.group,
		createdAt: evaluation ? leftImage.createdAt : rightImage.createdAt,
		updatedAt: evaluation ? leftImage.updatedAt : rightImage.updatedAt,
	};

	return { image: imageToReturn, keyToDelete, keyToSave };
}

/**
 * Given the id of an image document, deletes that document from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the image document
 */
export async function deleteImageDoc(id: string): Promise<void> {
	await db.image.delete(id);
}

/**
 * Given the id of an image document, queries and retrieves the image document
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the image doc
 * @returns The image document
 */
export async function getImageDoc(id: string): Promise<Image | undefined> {
	const imageDoc = await db.image.get(id);
	return imageDoc;
}

/**
 * Given the id of the image doc, retrieves it, then tries to get the image binary and attach it, returning the "built" image
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The image doc id
 * @returns The imageDoc, with the key set to the file the way the updateImageDoc module expects it
 */
export async function getImageDocWithBinaryKeyForUpdateImageDoc(id: string, getImageBinaries?: boolean): Promise<Image | null> {
	const retrievedImage = await getImageDoc(id);
	if (retrievedImage && typeof retrievedImage.key === "string") {
		if (getImageBinaries) {
			let imageBinary = await getImageBinaryById(retrievedImage.key);
			// If the binary was renamed to .webp by a prior sync, fall back to that key
			if (!imageBinary && retrievedImage.key && !retrievedImage.key.endsWith(".webp")) {
				imageBinary = await getImageBinaryById(retrievedImage.key + ".webp");
			}
			if (imageBinary) {
				retrievedImage.key = { files: [imageBinary.binary] };
			}
			// If binary not found, keep the original string key so the online fallback can still render it
		}
		return retrievedImage;
	}
	return null;
}

/**
 * Queries the count of all Images, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all images
 */
export async function getTotalImageDiskSize(): Promise<number> {
	const number = await db.image.count();
	const AVERAGE_DOCUMENT_SIZE = 185.2;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Given an image doc, saves it to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param image The image doc we want to save
 */
export async function saveImageDoc(image: Image): Promise<void> {
	await db.image.put(image);
}
